import type { QueryClient } from '@tanstack/react-query'
import type { HuntType, HuntsTab, OrgType } from '@/shared/domain'

export const CHANNELS_QUERY_ROOT = 'channels' as const
export const CONNECT_UNREAD_TOTAL_QUERY_ROOT = 'connect-unread-total' as const
export const HUNT_TYPE_ACTIVITY_QUERY_ROOT = 'hunt-type-activity' as const
export const DASHBOARD_SEASON_QUERY_ROOT = 'dashboard-season' as const
export const GALLERY_HUNT_PHOTOS_QUERY_ROOT = 'gallery-hunt-photos' as const
export const GALLERY_DOG_PHOTOS_QUERY_ROOT = 'gallery-dog-photos' as const
export const ANALYTICS_SEASONS_QUERY_ROOT = 'analytics-seasons' as const
export const ANALYTICS_FORM_DATA_QUERY_ROOT = 'analytics-form-data' as const
export const ANALYTICS_FILTERS_QUERY_ROOT = 'analytics-filters' as const
export const ANALYTICS_STATS_QUERY_ROOT = 'analytics-stats' as const
export const HUNT_FORM_DATA_QUERY_ROOT = 'hunt-form-data' as const
export const HUNT_LOGS_LIST_QUERY_ROOT = 'hunt-logs-list' as const
export const HUNT_LOGS_SEASON_CHIPS_QUERY_ROOT = 'hunt-logs-season-chips' as const

export const ORG_SCOPED_QUERY_KEY_ROOTS: readonly string[] = [
  CHANNELS_QUERY_ROOT,
  CONNECT_UNREAD_TOTAL_QUERY_ROOT,
  HUNT_TYPE_ACTIVITY_QUERY_ROOT,
  DASHBOARD_SEASON_QUERY_ROOT,
  GALLERY_HUNT_PHOTOS_QUERY_ROOT,
  GALLERY_DOG_PHOTOS_QUERY_ROOT,
  ANALYTICS_SEASONS_QUERY_ROOT,
  ANALYTICS_FORM_DATA_QUERY_ROOT,
  ANALYTICS_FILTERS_QUERY_ROOT,
  ANALYTICS_STATS_QUERY_ROOT,
  HUNT_FORM_DATA_QUERY_ROOT,
  HUNT_LOGS_LIST_QUERY_ROOT,
  HUNT_LOGS_SEASON_CHIPS_QUERY_ROOT,
]

export function channelsKey(orgId: string, orgType: 'club' | 'outfitter') {
  return [CHANNELS_QUERY_ROOT, orgId, orgType] as const
}

export function connectUnreadTotalKey(orgId: string, orgType: 'club' | 'outfitter') {
  return [CONNECT_UNREAD_TOTAL_QUERY_ROOT, orgId, orgType] as const
}

export function huntTypeActivityKey(orgId: string, orgType: OrgType) {
  return [HUNT_TYPE_ACTIVITY_QUERY_ROOT, orgId, orgType] as const
}

export function dashboardSeasonKey(orgId: string, orgType: OrgType, huntType: HuntType) {
  return [DASHBOARD_SEASON_QUERY_ROOT, orgId, orgType, huntType] as const
}

export function galleryHuntPhotosKey(
  orgId: string,
  orgType: 'club' | 'outfitter',
  userId: string,
) {
  return [GALLERY_HUNT_PHOTOS_QUERY_ROOT, orgId, orgType, userId] as const
}

export function galleryDogPhotosKey(orgId: string, orgType: 'club' | 'outfitter') {
  return [GALLERY_DOG_PHOTOS_QUERY_ROOT, orgId, orgType] as const
}

export function analyticsSeasonsKey(orgId: string, orgType: OrgType) {
  return [ANALYTICS_SEASONS_QUERY_ROOT, orgId, orgType] as const
}

export function analyticsFormDataKey(orgId: string, orgType: OrgType) {
  return [ANALYTICS_FORM_DATA_QUERY_ROOT, orgId, orgType] as const
}

export function analyticsFiltersKey(orgId: string, orgType: OrgType) {
  return [ANALYTICS_FILTERS_QUERY_ROOT, orgId, orgType] as const
}

export type AnalyticsStatKind =
  | 'club-stats'
  | 'species-breakdown'
  | 'blind-performance'
  | 'season-stats'
  | 'unassigned-birds'
  | 'top-hunters'
  | 'guide-performance'
  | 'per-type-metrics'
  | 'deer'
  | 'turkey'
  | 'gator'
  | 'hog'
  | 'upland'

export function analyticsStatsKey(
  kind: AnalyticsStatKind,
  orgId: string,
  orgType: OrgType,
  huntType: HuntType | undefined,
  seasonYear: number | undefined,
  propertyId: string | undefined,
  extra?: Record<string, string | undefined>,
) {
  return [
    ANALYTICS_STATS_QUERY_ROOT,
    orgId,
    orgType,
    kind,
    huntType ?? 'none',
    seasonYear ?? 0,
    propertyId ?? 'all',
    extra ?? {},
  ] as const
}

export function huntFormDataKey(orgId: string, orgType: OrgType) {
  return [HUNT_FORM_DATA_QUERY_ROOT, orgId, orgType] as const
}

export function huntLogsListKey(
  orgId: string,
  orgType: OrgType,
  tab: HuntsTab,
  page: number,
  huntType: HuntType | null,
  startDate: string | undefined,
  endDate: string | undefined,
) {
  return [
    HUNT_LOGS_LIST_QUERY_ROOT,
    orgId,
    orgType,
    tab,
    page,
    huntType ?? 'none',
    startDate ?? 'none',
    endDate ?? 'none',
  ] as const
}

export function huntLogsSeasonChipsKey(orgId: string, orgType: OrgType, huntType: HuntType) {
  return [HUNT_LOGS_SEASON_CHIPS_QUERY_ROOT, orgId, orgType, huntType] as const
}

export function clearOrgScopedQueries(client: QueryClient): void {
  client.removeQueries({
    predicate: (query) => ORG_SCOPED_QUERY_KEY_ROOTS.includes(query.queryKey[0] as string),
  })
}
